import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth-context";
import { Pill } from "../components/Pill";
import * as shared from "@ventrin/shared";
type ActivePlan = "early_access" | "starter" | "team" | "enterprise";
const resolvePlan = (p: any) => shared.resolvePlan(p);

type Role = "end_user" | "team_manager" | "workspace_admin";

const ROLE_LABELS: Record<Role, string> = {
  end_user: "End User",
  team_manager: "Team Manager",
  workspace_admin: "Workspace Admin",
};

interface CreatedInvite {
  inviteId: string;
  email?: string;
  link: string;
  expiresAt: number;
  role: Role;
}

export function UsersPage() {
  const { profile } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [invEmail, setInvEmail] = useState("");
  const [invTeam, setInvTeam] = useState<string>(profile.teams[0]?.id ?? "");
  const [invRole, setInvRole] = useState<Role>("end_user");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justCreated, setJustCreated] = useState<CreatedInvite | null>(null);
  const [copyOk, setCopyOk] = useState(false);

  const plan: ActivePlan = resolvePlan(profile.workspace?.plan);
  const planDef = shared.PLANS[plan];

  const load = useCallback(async () => {
    if (!profile.workspace) return;
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.listWorkspaceMembers(),
        api.listWorkspaceInvites(),
      ]);
      setUsers(u.users ?? []);
      setInvites(i.invites ?? []);
    } finally {
      setLoading(false);
    }
  }, [profile.workspace?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!profile.workspace) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load, profile.workspace?.id]);

  const outstandingInvites = useMemo(
    () => invites.filter((i) => !i.revokedAt && i.usageCount < i.usageLimit && i.expiresAt > Date.now()),
    [invites]
  );

  // Seat usage: existing members + unredeemed-and-unexpired invites.
  const seatsUsed = users.length + outstandingInvites.length;
  const seatCap = planDef.maxUsers;
  const atCap = Number.isFinite(seatCap) && seatsUsed >= seatCap;

  async function createInvite() {
    if (atCap) return;
    setBusy(true);
    setJustCreated(null);
    try {
      const res = await api.createInvite({
        email: invEmail || undefined,
        teamId: invTeam,
        usageLimit: 1,
        expiresInDays: 7,
        role: invRole,
      });
      const link = buildInviteLink(res.token);
      setJustCreated({
        inviteId: res.invite.id,
        email: res.invite.email,
        link,
        expiresAt: res.invite.expiresAt,
        role: res.invite.role,
      });
      setInvEmail("");
      await load();
    } catch (e: any) {
      const code = e?.details?.code ?? e?.code;
      if (code === "plan-upgrade-required") {
        alert(`Seat limit reached on the ${plan} plan. Open the Billing tab to upgrade.`);
      } else {
        alert(e?.message ?? "Could not create invite.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function revokeInvite(inviteId: string) {
    if (!confirm("Revoke this invite? The link will stop working immediately.")) return;
    await api.revokeInvite({ inviteId });
    if (justCreated?.inviteId === inviteId) setJustCreated(null);
    await load();
  }

  async function assignTeam(userId: string, teamId: string) {
    await api.assignUserTeam({ userId, teamId });
    await load();
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch {
      // fall back to selection
    }
  }

  return (
    <>
      <header className="sg-page-header">
        <div>
          <h1 className="sg-page-title">Users</h1>
          <div className="sg-page-sub">Invite people. Manage team membership and roles.</div>
        </div>
        <div className="sg-row" style={{ gap: 10, alignItems: "center" }}>
          <Pill kind={atCap ? "warn" : "neutral"}>
            {seatsUsed} / {Number.isFinite(seatCap) ? seatCap : "∞"} seats · {planDef.name}
          </Pill>
        </div>
      </header>

      {atCap && (
        <div className="sg-panel" style={{ marginBottom: 16, borderLeft: "3px solid var(--vt-amber, #b3791a)" }}>
          <div className="sg-panel-pad">
            <strong>Seat limit reached.</strong>{" "}
            You're on the {planDef.name} plan ({seatCap} seats). Upgrade in the{" "}
            <a href="#/billing">Billing tab</a> to invite more users.
          </div>
        </div>
      )}

      <div className="sg-panel" style={{ marginBottom: 16 }}>
        <div className="sg-panel-head"><div className="sg-panel-title">Create invite link</div></div>
        <div className="sg-panel-pad sg-row" style={{ gap: 10, flexWrap: "wrap" }}>
          <input
            className="sg-input"
            placeholder="Email (optional — scopes the invite to one address)"
            value={invEmail}
            onChange={(e) => setInvEmail(e.target.value)}
            style={{ minWidth: 280 }}
          />
          <select className="sg-select" value={invTeam} onChange={(e) => setInvTeam(e.target.value)}>
            {profile.teams.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="sg-select" value={invRole} onChange={(e) => setInvRole(e.target.value as Role)}>
            <option value="end_user">End User</option>
            <option value="team_manager">Team Manager</option>
            <option value="workspace_admin">Workspace Admin</option>
          </select>
          <button className="sg-btn sg-btn-primary" onClick={createInvite} disabled={busy || atCap}>
            Generate invite
          </button>
        </div>

        {justCreated && (
          <div className="sg-panel-pad" style={{ borderTop: "1px solid var(--vt-line, #e5e5e5)" }}>
            <div className="sg-muted" style={{ fontSize: 12, marginBottom: 6 }}>
              <strong>Copy this link now</strong> — for security, we won't show it again.
            </div>
            <div className="sg-row" style={{ gap: 8, alignItems: "stretch" }}>
              <input
                readOnly
                className="sg-input sg-mono"
                value={justCreated.link}
                onFocus={(e) => e.currentTarget.select()}
                style={{ flex: 1, minWidth: 320 }}
              />
              <button className="sg-btn sg-btn-primary" onClick={() => copy(justCreated.link)}>
                {copyOk ? "Copied" : "Copy link"}
              </button>
            </div>
            <div className="sg-muted" style={{ fontSize: 11, marginTop: 6 }}>
              Role: {ROLE_LABELS[justCreated.role]} · Expires {new Date(justCreated.expiresAt).toLocaleString()}
              {justCreated.email ? ` · Scoped to ${justCreated.email}` : " · Open invite (anyone with the link)"}
            </div>
          </div>
        )}
      </div>

      <div className="sg-grid sg-grid-2">
        <div className="sg-panel">
          <div className="sg-panel-head"><div className="sg-panel-title">Members ({users.length})</div></div>
          <div className="sg-panel-pad">
            {loading && users.length === 0 ? <div className="sg-empty">Loading…</div> : (
              <table className="sg-table">
                <thead><tr><th>Email</th><th>Role</th><th>Team</th><th>Extension</th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td><Pill kind="neutral">{ROLE_LABELS[u.role as Role] ?? u.role}</Pill></td>
                      <td>
                        <select
                          className="sg-select"
                          value={u.teamId ?? ""}
                          onChange={(e) => assignTeam(u.id, e.target.value)}
                        >
                          {profile.teams.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                      <td><Pill kind={u.extensionStatus === "active" ? "allow" : "neutral"}>{u.extensionStatus}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="sg-panel">
          <div className="sg-panel-head"><div className="sg-panel-title">Outstanding invites ({outstandingInvites.length})</div></div>
          <div className="sg-panel-pad sg-stack">
            {outstandingInvites.map((i) => (
              <div key={i.id} className="sg-card" style={{ gridTemplateColumns: "1fr auto" }}>
                <div className="sg-card-body">
                  <div className="sg-muted" style={{ fontSize: 12 }}>
                    {i.email || "Open invite"} · {ROLE_LABELS[i.role as Role] ?? i.role}
                  </div>
                  <div className="sg-mono" style={{ fontSize: 12, color: "var(--vt-ink-3, #6a6a6a)" }}>
                    {i.tokenPreview}… (full link visible once at creation)
                  </div>
                  <div className="sg-muted" style={{ fontSize: 11, marginTop: 4 }}>
                    Expires {new Date(i.expiresAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="sg-card-body" style={{ display: "flex", alignItems: "center" }}>
                  <button className="sg-btn" onClick={() => revokeInvite(i.id)}>Revoke</button>
                </div>
              </div>
            ))}
            {outstandingInvites.length === 0 && <div className="sg-empty">No outstanding invites.</div>}
          </div>
        </div>
      </div>
    </>
  );
}

function buildInviteLink(token: string): string {
  // The invite URL is rendered by the SignIn page (route #/sign-in?invite=...).
  // We use the current origin so admins can hand the same link to users on
  // any deployment (preview, staging, production).
  const origin = window.location.origin.replace(/\/$/, "");
  return `${origin}/#/sign-in?invite=${encodeURIComponent(token)}`;
}
